import { AttractImages, Configuration, FiscalFieldsName } from '@lib/models/config'
import { Block } from '@lib/models/blocks'
import { Cart } from '@lib/models/purchase'
import { Category } from '@lib/models/category'
import { scopedLogger, logInfo, logError } from '@lib/utils/logUtils'
import { defineStore } from 'pinia'
import { purchaseService } from '@lib/services/backend/purchase/purchase.service'
import { SaleType, PriceTotals } from '@lib/models/common'
import { ProductLiteCategorized } from '@lib/models/product'
import { FiscalFields } from '@lib/models/internal/checkout'
import { LoginInput, LoginType, User } from '@lib/models/identification'
import { identificationService } from '@lib/services/backend/identification/identification.service'
import { capitalizeName } from '@lib/utils/formatUtils'
import { useGtm } from '@composables/useGtm'
import { LoginMethod } from '@lib/models/gtm'
import { removeAllAnalyticsCookies } from '@lib/utils/utils'
import { LoyaltyTier } from '@lib/models/loyalty'
import { catalogService } from '@lib/services/backend/catalog/catalog.service'
import { ordersService } from '@lib/services/backend/orders/orders.service'
import { extractCustomError } from '@lib/utils/gtmUtils'
import { qrHasToken } from '@lib/utils/scannerUtils'
import type { PrinterErrorType } from '@lib/utils/printerUtils'

const log = scopedLogger('@stores/app.store.ts')

interface State {
  // The resolved locale for currency/number formatting — see resolveFormatLocale
  // in formatUtils.ts for how this differs from config.locale and the i18n locale.
  formatLocale: string
  mergedTranslationLocales: string[]
  config?: Configuration
  categories: Array<Category>
  accessibility: boolean
  resetADK: boolean
  // errors shown on the inoperative-printer screen. Not cleared by executeResetADK on purpose:
  // they are set right before it, when jumping to that screen from inside the flow.
  printerErrors: PrinterErrorType[]
  // view
  viewCategory?: Category
  // content
  homeBlocks?: Array<Block>
  // crossSelling
  crossSellingCheckoutIsViewed: boolean
  crossSellingProducts?: Array<ProductLiteCategorized>
  lastProductList: string
  // purchaseSession
  cart?: Cart
  fiscalFields?: FiscalFields
  cardMethodUnavailable: boolean
  // user
  user?: User
  // set while a soft user validates their IM identity by scanning QR (redirectTo = path to go after)
  softUserValidationImIdentity?: { redirectTo: string }
  confirmingIdentity: boolean
  inactivityIsShowed: boolean
  // last login by QR carried the IM token (4 params QR)
  lastLoginHasToken: boolean
  // OTP validation, per user session. nextRequestAt = ISO UTC ('...Z') given by backend,
  // the moment from which a new code can be requested
  otp: { nextRequestAt?: string }
  // aop coupons
  aopCouponProducts?: ProductLiteCategorized[]
  // loyalty
  loyaltyTiers: Array<LoyaltyTier> | undefined
}

export const useAppStore = defineStore('app', {
  state: (): State => ({
    formatLocale: '',
    mergedTranslationLocales: [],
    config: undefined,
    categories: [],
    accessibility: false,
    resetADK: false,
    printerErrors: [],
    viewCategory: undefined,
    homeBlocks: undefined,
    crossSellingCheckoutIsViewed: false,
    crossSellingProducts: undefined,
    lastProductList: '',
    cart: undefined,
    fiscalFields: undefined,
    cardMethodUnavailable: false,
    user: undefined,
    softUserValidationImIdentity: undefined,
    confirmingIdentity: false,
    inactivityIsShowed: false,
    lastLoginHasToken: false,
    otp: { nextRequestAt: undefined },
    aopCouponProducts: undefined,
    loyaltyTiers: undefined
  }),
  getters: {
    cartTotal(): number {
      if (!this.cart) return 0
      return this.cart.totals.total
    },
    cartTotals(): PriceTotals {
      if (!this.cart)
        return {
          total: 0,
          subtotal: 0,
          tax: 0,
          discount: 0,
          points: 0
        }
      return this.cart.totals
    },
    cartUnits(): number {
      if (!this.cart) return 0
      return this.cart.productsTotals.reduce((acc, prodGroup) => {
        acc += prodGroup.units
        return acc
      }, 0)
    },
    attractImages(): AttractImages | undefined {
      if (!this.config) return undefined
      return this.config?.attractScreen.images
    },
    documentMask(): string {
      if (!this.config?.dynamicFiscalFields) return ''
      const typeFieldOptions = this.config.dynamicFiscalFields.find(
        (fiscalField) => fiscalField.code === FiscalFieldsName.DOCUMENT_TYPE
      )?.options

      const mask = typeFieldOptions?.find(
        (data) => data.defaultLoyaltyDocument && data.loyaltyDocument
      )?.mask

      return mask ?? ''
    },
    currencyAcronym(): string {
      return this.config?.currencyAcronym ?? ''
    },
    hideCombos(): boolean {
      return this.config?.hideCombos ?? false
    },
    isUserFullExp(): boolean {
      return this.user?.loginType === LoginType.IM
    },
    // which identity check must pass before going to checkout (checked in this order)
    identityCheckBeforeCheckout(): 'otp' | 'confirm' | undefined {
      const spendingPoints = !!this.cartTotals.points
      if (!this.isUserFullExp || !spendingPoints) return undefined
      if (!this.user?.verified && !this.lastLoginHasToken) return 'otp'
      if (this.inactivityIsShowed && this.lastLoginHasToken) return 'confirm'
      return undefined
    }
  },
  actions: {
    async executeResetADK() {
      if (this.cart?.sessionId) {
        try {
          // end purchaseSession & dispose order (if needed in back)
          await this.endPurchaseSession(this.cart.sessionId)
        } catch {
          this.setCart() // to undefined
        }
      }
      // reset store
      this.homeBlocks = undefined
      this.crossSellingProducts = undefined
      this.crossSellingCheckoutIsViewed = false
      this.lastProductList = ''
      this.accessibility = false
      this.cardMethodUnavailable = false
      this.loyaltyTiers = undefined
      this.mergedTranslationLocales = []
      // user related
      await this.clearUserData({ cleanSession: false })
      this.softUserValidationImIdentity = undefined
      this.confirmingIdentity = false
      this.inactivityIsShowed = false
      logInfo(log, 'RESETTED ADK STATE succesfully')
      // remove _ga and clarity cookies
      removeAllAnalyticsCookies()
      this.resetADK = false
    },
    setConfiguration(config?: Configuration) {
      this.config = config
      logInfo(log, 'Setted configuration')
    },
    setCategories(categories: Array<Category>) {
      this.categories = categories
      logInfo(log, 'Setted categories')
    },
    setAccessibility(val: boolean) {
      this.accessibility = val
    },
    setPrinterErrors(errors: PrinterErrorType[]) {
      this.printerErrors = errors
    },
    // views
    setViewCategory(category?: Category) {
      this.viewCategory = category
    },
    // content
    setHomeBlocks(homeBlocks: Array<Block>) {
      this.homeBlocks = homeBlocks
    },
    // crossSelling
    setCrossSellingProducts(products: Array<ProductLiteCategorized>, lastProductList: string) {
      this.crossSellingProducts = products
      this.lastProductList = lastProductList
    },
    setCrossSellingCheckoutIsViewed() {
      this.crossSellingCheckoutIsViewed = true
    },

    // purchaseSession
    setCart(cart?: Cart) {
      this.cart = cart
      logInfo(log, 'Setted cart')
    },
    async initPurchaseSession(saleType: SaleType) {
      logInfo(log, 'fetch purchaseSession start')
      try {
        this.cart = await purchaseService.fetchStartPurchaseSession(saleType)
        logInfo(
          log,
          'purchaseSession started with success, sessionId = ' +
            this.cart.sessionId +
            ', saleType = ' +
            this.cart.saleType
        )
      } catch (error) {
        logInfo(log, 'Something was wrong on starting purchaseSession')
        throw error
      }
    },
    async endPurchaseSession(sessionId: string) {
      logInfo(log, 'fetch purchaseSession end')
      try {
        await purchaseService.fetchEndPurchaseSession(sessionId)
        this.setCart() // to undefined
        logInfo(log, 'purchaseSession ended with success')
        logInfo(log, `Disposing order after ended session, with id = {${sessionId}}`)
        // will not await
        ordersService
          .fetchDisposeOrder(sessionId)
          .then((res) => {
            logInfo(
              log,
              `Try to dispose order={${sessionId}} async succesfully: ` + JSON.stringify(res ?? {})
            )
          })
          .catch((err) => {
            logError(
              log,
              `Some error in disposing order={${sessionId}} async : ` +
                (err?.response?.data ? JSON.stringify(err.response.data) : err.message)
            )
          })
      } catch {
        throw new Error('purchaseSession could not be ended')
      }
    },
    setFiscalFields(fiscalFields?: FiscalFields) {
      this.fiscalFields = fiscalFields
      logInfo(log, 'Setted fiscal fields => ' + JSON.stringify(fiscalFields || {}))
    },
    setUser({ user, qr }: { user?: User; qr?: string }) {
      if (user) {
        user.name = capitalizeName(user.name)
        if (qr) {
          // login by QR is always full experience (IM), even if backend could not log with IM (QR without token)
          user.loginType = LoginType.IM
          this.lastLoginHasToken = qrHasToken(qr)
        }
      }
      if (!user || user.mcId !== this.user?.mcId) this.resetOtp()
      this.user = user
    },
    setOtpNextRequestAt(nextRequestAt?: string) {
      this.otp = { nextRequestAt }
    },
    resetOtp() {
      this.otp = { nextRequestAt: undefined }
    },
    async logInUserAction(input: LoginInput) {
      const { trackLoginError } = useGtm()
      try {
        const user = await identificationService.logInUser(input)
        this.setUser({ user, qr: input.qr })
        return this.user
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const { type } = extractCustomError({ error })
        trackLoginError({
          errorType: type,
          method: input.qr ? LoginMethod.QR_IDENTIFICATION : LoginMethod.COUPON
        })
        logInfo(log, `User data could not be obtained: ${type}`)
        return undefined
      }
    },
    async clearUserData({ cleanSession = true }) {
      this.fiscalFields = undefined
      // logout user previously
      if (this.user) {
        const mcId = this.user.mcId
        try {
          await identificationService.logoutUser(mcId)
          logInfo(log, `Logout user succesfully with mcId = {${mcId}}`)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
          logError(
            log,
            `Some error in logout with mcId = {${mcId}}: ` + err?.response?.data
              ? JSON.stringify(err.response.data)
              : err.message
          )
        }
      }
      this.setUser({ user: undefined })
      this.lastLoginHasToken = false
      this.resetOtp()
      this.inactivityIsShowed = false

      if (cleanSession && (this.cart?.coupons.length || this.cart?.totals.points)) {
        // clear coupons and loyalty products from purchaseSession
        try {
          logInfo(log, 'Clearing coupons, loyalty products from purchase session')
          this.cart = await purchaseService.fetchCleanUserRelatedPurchaseSession(
            this.cart.sessionId
          )
        } catch {
          logInfo(
            log,
            'There was an error clearing purchaseSession coupons coupons, loyalty products'
          )
        }
      }
      logInfo(log, 'Cleared user data')
    },
    findCartProduct(productCartId: string) {
      return this.cart?.productsTotals.find(
        (cartProduct) => cartProduct.productCartId === productCartId
      )
    },
    setAopCouponProducts(products?: ProductLiteCategorized[]) {
      this.aopCouponProducts = products
    },
    setCardMethodUnavailable(val: boolean) {
      this.cardMethodUnavailable = val
    },
    async fetchLoyaltyTiers() {
      if (this.loyaltyTiers) return
      try {
        logInfo(log, 'Fetching async loyalty tiers')
        const loyaltyTiers = await catalogService.fetchLoyaltyCatalog()
        this.loyaltyTiers = loyaltyTiers
      } catch {
        logInfo(log, 'Loyalty tiers could not be loaded')
        this.loyaltyTiers = []
      }
    },
    // user is soft, click on loyalty product, must validate im identity with QR
    setSoftUserValidationImIdentity(validation?: { redirectTo: string }) {
      this.softUserValidationImIdentity = validation
    },
    // user is fullExp, inactivityIsShowed, on cart while redeeming points, shows ConfirmingIdentity
    setConfirmingIdentity(confirming: boolean) {
      this.confirmingIdentity = confirming
    },
    setInactivityIsShowed(isShowed: boolean) {
      this.inactivityIsShowed = isShowed
    },
    // remote translations cache, cleared on executeResetADK
    hasMergedTranslations(locale: string) {
      return this.mergedTranslationLocales.includes(locale)
    },
    // Marks the locale as "attempted", not "succeeded": callers invoke this
    // from a .finally() regardless of fetch outcome, by design, so a possibly-down Back
    // isn't hammered again on every language toggle before the next ADK reset.
    markTranslationsMerged(locale: string) {
      if (!this.hasMergedTranslations(locale)) {
        this.mergedTranslationLocales.push(locale)
      }
    }
  }
})
